import { useState, type FormEvent } from 'react';

import {
  CAVEAT_UNREGISTERED,
  CAVEAT_UNSIGNED,
  DEFAULT_PLAN_MAX_DOCUMENTS,
  INGEST_SKIP_REASONS,
  MAX_PLAN_MAX_DOCUMENTS,
  type ExcludedFile,
  type IngestExclusionReason,
  type IngestPlannerReason,
  type IngestSkipReason,
  type PlanSharepointIngestResponse,
  type SkippedDocument,
} from '@clm/shared-types';

import { bulkIngestAffordance, CLM_ADMIN_GROUP_NAME, CLM_CONTRIBUTOR_GROUP_NAME } from '../auth/permissions';
import { ErrorNotice } from '../components/ErrorNotice';
import { useIdentity } from '../auth/useIdentity';
import { requireSurface } from '../routes/surfaces';
import { CorrectionEditor } from './ingest/CorrectionEditor';
import { ReceiptCard, ToIngestCard, type CorrectedRow } from './ingest/IngestExecute';
import {
  executeSelected,
  planFromForm,
  replan,
  reopenRow,
  setRowTags,
  skipRow,
  startOver,
} from './ingest/planner-actions';
import {
  isCorrection,
  rowKey,
  rowTags,
  selectRows,
  setConfirming,
  setFolderLink,
  setIncludeUnsigned,
  setMaxDocuments,
  setOverride,
  useIngestPlannerStore,
  type RowOverride,
} from './ingest/planner-store';

/**
 * The totals `<dd>` tooltip repeats the caveat's first sentence (CLM-63). Derived
 * from the shared constant, never re-typed, so a wording change lands here too.
 */
const CAVEAT_UNREGISTERED_SUMMARY = CAVEAT_UNREGISTERED.slice(
  0,
  CAVEAT_UNREGISTERED.indexOf('. ') + 1,
);

/** Same derivation for the unsigned caveat (CLM-67); it is one sentence, so the whole of it. */
const CAVEAT_UNSIGNED_SUMMARY = CAVEAT_UNSIGNED.includes('. ')
  ? CAVEAT_UNSIGNED.slice(0, CAVEAT_UNSIGNED.indexOf('. ') + 1)
  : CAVEAT_UNSIGNED;

/**
 * Bulk ingest planner — CLM-43, with approve-and-execute since CLM-67, open to
 * Contributor as well as Admin since R-94-1 (CLM-94).
 *
 * An admin or contributor pastes the sharing link SharePoint's "Copy link"
 * produces for a folder of signed contracts; the gateway resolves and crawls it server-side
 * (rule 11 — the browser never speaks to Graph) and returns a **plan**: what a
 * backfill would ingest, what it would skip and why, and what the Signed + PDF
 * filter passed over. The plan itself ingests nothing — `dry_run` is
 * `z.literal(true)` in the contract, so "plan and also execute" is not even a
 * representable response.
 *
 * Executing is a second, explicit call (CLM-50's `execute-sharepoint-ingest`):
 * the caller ticks the rows they approve — a row planned without the signed
 * marker is never pre-ticked (D-67-2) — confirms, and the SPA posts the
 * `(drive_id, item_id, etag)` triples and nothing else (D-67-1). The server
 * re-crawls, re-plans and writes only the triples the fresh plan still wants.
 * See `./ingest/IngestExecute.tsx`. The `include_unsigned_pdfs` option (D-67-3)
 * is sent on the plan and mirrored on execute; default off, so a run without it
 * is byte-identical to CLM-43's.
 *
 * The state — form, plan, ticks, the execute in flight, the receipt — is NOT
 * component state. It lives in `./ingest/planner-store.ts` (D-67-6), so the
 * surface survives a NavRail switch and a receipt that arrives while the caller
 * is on another page is waiting on their return. This component renders the
 * store and forwards user actions to `./ingest/planner-actions.ts`; it owns no
 * promise and no `useState`.
 *
 * The skip reasons are the entire point of the dry run (rule 7 — surface
 * decisions, don't make them): each group below names something a human can fix
 * or must decide before several hundred extractions are set running.
 *
 * CLM-122 (D-110-4 point 4) — a file whose name gives no type, or contradicts
 * itself, is no longer a skip: it is offered as Type pending, never pre-ticked,
 * and the counts list says how many each reason holds and how many extraction
 * calls they would cost (R-122-10, R-122-11). It can be skipped like a skip row.
 */

/* -------------------------------------------------------------------------- */
/* Copy for the contract's vocabularies. Typed Records over the enums so a new  */
/* reason in @clm/shared-types is a compile error here, never a blank row.      */
/* -------------------------------------------------------------------------- */

const SKIP_REASON_COPY: Readonly<
  Record<IngestSkipReason, { readonly label: string; readonly explains: string }>
> = {
  party_key_not_derivable: {
    label: 'Party key not derivable',
    explains:
      'The folder yields no party key, and nothing is invented in its place (B-24) — no fallback to an account name, nothing guessed. Fix the party record at source, then plan again. This is the single most likely reason a backfill stalls.',
  },
  /* CLM-122 (R-122-14) — a name with no type no longer lands in these two: it is offered as Type pending. */
  sow_number_unusable: {
    label: 'SOW number unusable',
    explains:
      'The name says SOW — a SOW word, or a SOW-shaped number in a folder — but neither the file name nor any folder yields a usable SOW number. A file whose name gives no type at all is offered as Type pending instead.',
  },
  sow_number_not_matched: {
    label: 'SOW number not matched',
    explains:
      'The name says SOW, and a candidate number was recognised, but it matches no SOW number CLM knows about.',
  },
  sow_number_ambiguous: {
    label: 'SOW number ambiguous',
    explains:
      'Two or more known SOW numbers match the candidate. A human decides which — the system never resolves this by picking one.',
  },
  already_ingested: {
    label: 'Already ingested',
    explains:
      'The same document version (drive, item, eTag) was taken by a previous run. Skipping it is idempotency working, not a loss.',
  },
  no_etag: {
    label: 'No eTag from SharePoint',
    explains:
      'Graph returned no eTag for the item, so it cannot be keyed for idempotency and a re-run could not tell whether it changed.',
  },
  /* CLM-88 (D-88-2) — prefix and folder disagree; the row keeps both keys in `candidates`. */
  party_ambiguous: {
    label: 'Client is ambiguous',
    explains:
      'The SOW-number prefix in the file name and the folder name resolve to two different clients. Pick one with Correct and include.',
  },
};

/**
 * CLM-122 (R-122-6) — what the planner listed a skipped document under: a skip
 * reason, or the Type pending reason of a planned row the reviewer skipped.
 */
const PLANNER_REASON_LABEL: Readonly<Record<IngestPlannerReason, string>> = {
  party_key_not_derivable: SKIP_REASON_COPY.party_key_not_derivable.label,
  sow_number_unusable: SKIP_REASON_COPY.sow_number_unusable.label,
  sow_number_not_matched: SKIP_REASON_COPY.sow_number_not_matched.label,
  sow_number_ambiguous: SKIP_REASON_COPY.sow_number_ambiguous.label,
  already_ingested: SKIP_REASON_COPY.already_ingested.label,
  no_etag: SKIP_REASON_COPY.no_etag.label,
  party_ambiguous: SKIP_REASON_COPY.party_ambiguous.label,
  no_name_type: 'No type in the name (Type pending)',
  conflicting_names: 'Names conflict (Type pending)',
};

const EXCLUSION_REASON_COPY: Readonly<Record<IngestExclusionReason, string>> = {
  no_signed_marker: 'No “Signed” marker in the file name',
  signed_marker_inside_another_word: '“signed” is part of another word',
  not_pdf: 'Not a PDF',
  unusable_file_name: 'Unusable file name',
};

/* -------------------------------------------------------------------------- */
/* Surface                                                                      */
/* -------------------------------------------------------------------------- */

export function IngestPlanner() {
  const surface = requireSurface('ingest');
  const { identity, isAuthenticated } = useIdentity();
  const affordance = bulkIngestAffordance(identity, isAuthenticated);

  // Everything below is read from the planner store (D-67-6) — see the header.
  const {
    form,
    field_error: fieldError,
    phase,
    selected,
    confirming,
    overrides,
    skipped_now: skippedNow,
  } = useIngestPlannerStore();

  const loading = phase.kind === 'loading';
  const executing = phase.kind === 'executing';
  const busy = loading || executing;

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    planFromForm();
  }

  return (
    <>
      <div className="hd">
        <div>
          <h2>{surface.label}</h2>
          <p>{surface.summary}</p>
        </div>
      </div>

      {affordance === 'not_eligible' ? (
        /*
         * Honest affordance, not access control: the route stays reachable and
         * this notice loads. The API is the boundary either way (NFR-06) — it
         * answers 404 to anyone without `contract:bulk_ingest`.
         */
        <div className="stub">
          <span className="eyebrow">Contributor or Admin only</span>
          <p>
            Planning a bulk ingest requires the <code>{CLM_CONTRIBUTOR_GROUP_NAME}</code> or{' '}
            <code>{CLM_ADMIN_GROUP_NAME}</code> role, and your sign-in token does not show either
            group. Pointing the system at a folder of contracts is roughly four years of volume in
            one action, so it is a privilege of its own (R-94-1) — not part of the ordinary upload
            permission, though it is now held alongside it. If you believe you should hold it, ask
            whoever manages CLM access; if you were granted it recently, sign out and back in so
            your token picks it up.
          </p>
        </div>
      ) : (
        <>
          <form className="card" onSubmit={onSubmit}>
            <h3>Plan a backfill</h3>
            <p>
              Paste the sharing link that SharePoint’s “Copy link” produces for the folder holding
              signed contracts. The API resolves and crawls it server-side — this app never touches
              SharePoint — and returns a plan of what a backfill would do.
            </p>
            {affordance === 'unknown' ? (
              <p className="mut">
                Your token’s group claims don’t carry readable names, so this page cannot tell
                whether you hold <code>{CLM_CONTRIBUTOR_GROUP_NAME}</code> or{' '}
                <code>{CLM_ADMIN_GROUP_NAME}</code>. The API decides on every request; if you don’t
                hold either, planning will be refused.
              </p>
            ) : null}

            <div className="frow">
              <div className="field grow">
                <label htmlFor="ingest-folder-link">SharePoint folder link</label>
                <input
                  id="ingest-folder-link"
                  name="folder_link"
                  type="url"
                  required
                  spellCheck={false}
                  autoComplete="off"
                  placeholder="https://… (paste from “Copy link”)"
                  value={form.folder_link}
                  onChange={(event) => setFolderLink(event.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="field">
                <label htmlFor="ingest-max-documents">Document cap (optional)</label>
                <input
                  id="ingest-max-documents"
                  name="max_documents"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder={`blank = ${DEFAULT_PLAN_MAX_DOCUMENTS}`}
                  value={form.max_documents}
                  onChange={(event) => setMaxDocuments(event.target.value)}
                  disabled={busy}
                />
              </div>
              <button className="btn pri" type="submit" disabled={busy}>
                {loading ? 'Planning…' : 'Plan ingest'}
              </button>
            </div>
            {fieldError ? (
              <p className="ferr" role="alert">
                {fieldError}
              </p>
            ) : null}

            <div className="field opt">
              <label className="check">
                <input
                  id="ingest-include-unsigned"
                  name="include_unsigned_pdfs"
                  type="checkbox"
                  checked={form.include_unsigned}
                  onChange={(event) => setIncludeUnsigned(event.target.checked)}
                  disabled={busy}
                />{' '}
                Include PDFs without a ‘signed’ marker (review each one)
              </label>
              <p className="mut">
                Off by default: a PDF named without the word is usually a draft. On, each such file
                appears as a candidate tagged ‘no signed marker’ and is never pre-ticked.
              </p>
            </div>

            <p className="mut">
              A plan is a dry run. Tick the rows you approve and press Execute; only those rows are
              ingested, each under the exact version (eTag) you saw. The accuracy pilot uses a cap
              of 20.
            </p>
          </form>

          {phase.kind === 'loading' ? (
            <div className="card" role="status">
              <h3>Enumerating the folder</h3>
              <p>
                The API is crawling the folder tree in SharePoint, breadth-first, applying the
                “Signed” + PDF filter and matching SOW numbers and parties as it goes. A
                backfill-sized folder holds roughly 500 documents, so this can take a while.
                Nothing is being ingested — the result is a plan.
              </p>
            </div>
          ) : null}

          {phase.kind === 'session_expired' ? (
            <div className="card" role="alert">
              <h3>Session expired</h3>
              <p>Your sign-in is no longer valid. Redirecting to Entra to sign in again…</p>
            </div>
          ) : null}

          {phase.kind === 'executing' ? (
            <div className="card" role="status">
              <h3>Executing {phase.approved.length.toLocaleString()} approved documents</h3>
              <p>
                The API is re-crawling the folder, re-planning it and writing only the rows you
                approved that the fresh plan still wants — each downloaded once to verify, never
                stored by this page. A receipt follows.
              </p>
            </div>
          ) : null}

          {/* A sign-in denial is not this endpoint's role gate: the gateway
              refused the caller at step 4, before the route, and refuses every
              other call too. The card's role sentence would be false — it told
              a CEQ-CLM-Admin member with no scope group that she needs the
              Admin role (30 Sep 2026) — so the cause's own copy replaces it. */}
          {phase.kind === 'error' && phase.denial !== null && phase.denial !== 'role' ? (
            <ErrorNotice kind={phase.denial} correlation_id={phase.correlation_id} />
          ) : phase.kind === 'error' ? (
            <div className="card" role="alert">
              <h3>
                {phase.denial === 'role'
                  ? 'The API declined this request'
                  : phase.action === 'execute'
                    ? 'The execute request failed'
                    : 'The plan request failed'}
              </h3>
              <p>{phase.message}</p>
              {phase.field_errors.length > 0 ? (
                <ul className="ferrs">
                  {phase.field_errors.map((entry) => (
                    <li key={`${entry.path}:${entry.message}`}>
                      <code>{entry.path}</code> — {entry.message}
                    </li>
                  ))}
                </ul>
              ) : null}
              {phase.denial === 'role' ? (
                <p>
                  {phase.action === 'execute' ? 'Executing' : 'Planning'} a bulk ingest requires the{' '}
                  <code>{CLM_CONTRIBUTOR_GROUP_NAME}</code> or <code>{CLM_ADMIN_GROUP_NAME}</code>{' '}
                  role, and the endpoint answers only to accounts that hold one — to anyone else it
                  does not exist. If you were granted the role recently, sign out and back in so
                  your token picks it up.
                </p>
              ) : null}
              {phase.correlation_id ? (
                <p className="mut">
                  Correlation id <code>{phase.correlation_id}</code> — quote it when reporting this.
                </p>
              ) : null}
            </div>
          ) : null}

          {phase.kind === 'plan' || phase.kind === 'executing' ? (
            <PlanResult
              plan={phase.plan}
              busy={busy}
              selected={selected}
              confirming={confirming}
              overrides={overrides}
              skippedNow={skippedNow}
            />
          ) : phase.kind === 'error' && phase.kept !== null ? (
            // The plan and the store's ticks survive the failure under the problem card.
            <PlanResult
              plan={phase.kept.plan}
              busy={busy}
              selected={selected}
              confirming={confirming}
              overrides={overrides}
              skippedNow={skippedNow}
            />
          ) : null}

          {phase.kind === 'receipt' ? (
            <ReceiptCard
              receipt={phase.receipt}
              approved={phase.approved}
              planned={phase.plan.to_ingest}
              skippedPlan={phase.plan.to_skip}
              onReplan={() => replan(phase.request)}
              onStartOver={startOver}
            />
          ) : null}
        </>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Plan rendering                                                              */
/* -------------------------------------------------------------------------- */

function PlanResult({
  plan,
  busy,
  selected,
  confirming,
  overrides,
  skippedNow,
}: {
  readonly plan: PlanSharepointIngestResponse;
  readonly busy: boolean;
  readonly selected: ReadonlySet<string>;
  readonly confirming: boolean;
  readonly overrides: ReadonlyMap<string, RowOverride>;
  /** CLM-115 — rows skipped in this sitting, so the cards move without a re-crawl. */
  readonly skippedNow: ReadonlySet<string>;
}) {
  const { totals, root } = plan;

  /* CLM-71 — a corrected skip row moves to the ingest card; the rest stay here. */
  const corrected: CorrectedRow[] = [];
  const stillSkipped: SkippedDocument[] = [];
  for (const row of plan.to_skip) {
    /* CLM-115 — skipped during this plan, so it has left the review list. The
     * server already holds the decision; this only spares a re-crawl. */
    if (skippedNow.has(rowKey(row))) continue;
    const override = overrides.get(rowKey(row));
    /* CLM-77 — a tags-only entry is not a correction; the row stays skipped. */
    if (override === undefined || !isCorrection(override)) stillSkipped.push(row);
    else corrected.push({ row, override });
  }

  /* CLM-115 — the Skipped section: what the server held back, plus anything
   * skipped in this sitting. The two cannot overlap, because a row the server
   * held back never reached `to_skip`. CLM-122 (R-122-6): a Type pending row
   * skipped in this sitting leaves `to_ingest` for the same list. */
  const skippedThisPlan: SkippedThisPlanRow[] = [
    ...plan.to_skip
      .filter((row) => skippedNow.has(rowKey(row)))
      .map((row) => ({ ...skippedRowOf(row), reason: row.reason })),
    ...plan.to_ingest.flatMap((row) =>
      row.type_pending_reason !== null && skippedNow.has(rowKey(row))
        ? [{ ...skippedRowOf(row), reason: row.type_pending_reason }]
        : [],
    ),
  ];
  const toIngest = plan.to_ingest.filter((row) => !skippedNow.has(rowKey(row)));

  return (
    <>
      {/* D-67-6 — the plan and the ticks are kept while the admin visits other
          pages; only Start over, a page refresh or sign-out clears them. Kept
          disabled while a request is in flight so a receipt is never discarded
          by accident. */}
      <div className="selbar retain" role="toolbar" aria-label="Kept plan">
        <span className="mut">
          This plan and your ticks stay here while you visit other pages. Refreshing the browser or
          signing out clears them.
        </span>
        <button type="button" className="btn sm qt" disabled={busy} onClick={startOver}>
          Start over
        </button>
      </div>

      {plan.truncated ? (
        <p className="banner bad" role="alert">
          Truncated — the crawl stopped at the document cap with folders still unvisited. These
          numbers describe the documents reached breadth-first before the cap, not the whole
          folder. Raise the cap (up to {MAX_PLAN_MAX_DOCUMENTS.toLocaleString()}) and plan again
          for the full picture.
        </p>
      ) : null}

      {totals.exceeds_design_volume ? (
        <p className="banner up">
          This one run would ingest more than a month of design volume (~10 contracts/month), the
          figure several ratified decisions rest on. Expected for a backfill — but if routine runs
          keep exceeding it, architecture §0.1 needs re-opening.
        </p>
      ) : null}

      <div className="stats">
        <StatTile label="Files found" value={totals.files_seen} />
        <StatTile label="To ingest" value={totals.to_ingest} />
        {/* CLM-122 — offered with no type, for the AI to read after filing; never pre-ticked. */}
        <StatTile label="Type pending" value={totals.type_pending} />
        <StatTile label="To skip" value={totals.to_skip} />
        <StatTile label="Excluded by filter" value={totals.excluded_files} />
      </div>

      <div className="card">
        <h3>Scanned root</h3>
        <p>
          {root.web_url ? (
            <a href={root.web_url} target="_blank" rel="noreferrer">
              {root.name}
            </a>
          ) : (
            root.name
          )}
          {root.is_folder ? null : ' — the pasted link addressed a single file, not a folder.'}
        </p>
        <dl className="counts">
          <div>
            <dt>Folders visited</dt>
            <dd>{totals.folders_visited.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Documents matched</dt>
            <dd>{totals.documents_matched.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Manual entry (no LLM)</dt>
            <dd>{totals.manual_entry.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Party key not derivable</dt>
            <dd>{totals.party_key_not_derivable.toLocaleString()}</dd>
          </div>
          <div>
            <dt>SOW number needs a human — not filed and not extracted until a number is confirmed (D-71-0). Correction: CLM-71.</dt>
            <dd>{totals.sow_number_needs_resolution.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Unregistered SOW numbers</dt>
            <dd title={CAVEAT_UNREGISTERED_SUMMARY}>
              {totals.sow_number_unregistered.toLocaleString()}
            </dd>
          </div>
          <div>
            <dt>Unsigned candidates</dt>
            <dd title={CAVEAT_UNSIGNED_SUMMARY}>{totals.unsigned_candidates.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Already ingested</dt>
            <dd>{totals.already_ingested.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Changed since last ingest</dt>
            <dd>{totals.changed_since_ingest.toLocaleString()}</dd>
          </div>
          {/* CLM-122 (R-122-10, R-122-11) — the counts by reason D-110-4 (a) promised before anything is filed. */}
          <div>
            <dt>No type in the name — offered, not ticked</dt>
            <dd>{totals.type_pending_by_reason.no_name_type.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Names conflict — filed as Type pending</dt>
            <dd>{totals.type_pending_by_reason.conflicting_names.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Extraction calls if every Type pending row is ticked</dt>
            <dd title="One extraction each, none for a restricted client; no extra call for the type. Clause and embedding calls, which any filed contract may get, are not counted.">
              {totals.type_pending_extraction_calls.toLocaleString()}
            </dd>
          </div>
        </dl>
      </div>

      {totals.files_seen === 0 ? (
        <div className="stub">
          <span className="eyebrow">Empty result</span>
          <p>
            The link resolved and the crawl ran, but there are no files beneath it. Check that the
            link points at the folder that holds the signed contracts, and that the account the
            gateway runs as can see it.
          </p>
        </div>
      ) : (
        <>
          <ToIngestCard
            documents={toIngest}
            busy={busy}
            selected={selected}
            confirming={confirming}
            onSelect={selectRows}
            onConfirm={setConfirming}
            onExecute={executeSelected}
            corrected={corrected}
            onRemoveCorrection={(key) => setOverride(key, null)}
            tagsOf={(key) => rowTags(overrides, key)}
            onSetTags={setRowTags}
            onSkipTypePending={(doc) => {
              /* CLM-122 (R-122-6) — recorded with its Type pending reason, verbatim. */
              if (doc.type_pending_reason === null) return;
              void skipRow({ drive_id: doc.drive_id, item_id: doc.item_id, etag: doc.etag, reason: doc.type_pending_reason });
            }}
          />
          <ToSkipCard documents={stillSkipped} total={plan.to_skip.length} busy={busy} />
          <SkippedCard
            held={plan.user_skipped}
            skippedThisPlan={skippedThisPlan}
            busy={busy}
          />
          <ExcludedCard files={plan.excluded_files} />
        </>
      )}

      <p className="mut">
        Dry run — nothing has been ingested yet. This access was audited (NFR-07): audit{' '}
        <code>{plan.audit.audit_id}</code>, correlation <code>{plan.audit.correlation_id}</code>,
        at {plan.audit.occurred_at_utc}.
      </p>
    </>
  );
}

/**
 * CLM-122 (R-122-6) — a row skipped in this sitting, from `to_skip` or (Type
 * pending) from `to_ingest`: only what the Skipped card shows and Reopen needs.
 */
interface SkippedThisPlanRow {
  readonly drive_id: string;
  readonly item_id: string;
  readonly web_url: string | null;
  readonly file_name: string;
  readonly folder_path: readonly string[];
  readonly reason: IngestPlannerReason;
}

function skippedRowOf(row: Omit<SkippedThisPlanRow, 'reason'>): Omit<SkippedThisPlanRow, 'reason'> {
  return {
    drive_id: row.drive_id,
    item_id: row.item_id,
    web_url: row.web_url,
    file_name: row.file_name,
    folder_path: row.folder_path,
  };
}

function StatTile({ label, value }: { readonly label: string; readonly value: number }) {
  return (
    <div className="stat">
      <span>{label}</span>
      <b>{value.toLocaleString()}</b>
    </div>
  );
}

function FileCell({
  file_name,
  web_url,
  folder_path,
}: {
  readonly file_name: string;
  readonly web_url: string | null;
  readonly folder_path: readonly string[];
}) {
  return (
    <td>
      {web_url ? (
        <a href={web_url} target="_blank" rel="noreferrer">
          {file_name}
        </a>
      ) : (
        file_name
      )}
      {folder_path.length > 0 ? <span className="path">{folder_path.join(' / ')}</span> : null}
    </td>
  );
}

function ToSkipCard({
  documents,
  total,
  busy,
}: {
  /** The skipped rows still on this card — corrected ones have moved to the ingest card (CLM-71). */
  readonly documents: readonly SkippedDocument[];
  /** The plan's own `to_skip` count, so the heading matches the plan the API returned. */
  readonly total: number;
  readonly busy: boolean;
}) {
  // Grouped in the contract's own order, so two plans read the same way.
  const groups = INGEST_SKIP_REASONS.map((reason) => ({
    reason,
    rows: documents.filter((doc) => doc.reason === reason),
  })).filter((group) => group.rows.length > 0);

  /**
   * CLM-71 — which row's "Correct and include" editor is open; one at a time.
   * Transient UI, not planner state: closing it on a NavRail switch loses
   * nothing the store has not already been told.
   */
  const [editing, setEditing] = useState<string | null>(null);
  const correctedAway = total - documents.length;

  return (
    <div className="card">
      <h3>To skip — {total.toLocaleString()}</h3>
      {total === 0 ? (
        <p>Nothing skipped — every matched document is in the ingest set.</p>
      ) : (
        <>
          <p>
            These matched the filter but will not be ingested. The reasons are the point of the dry
            run: each group names what a human can fix or must decide before the real run. Where the
            planner could not derive the SOW number or the client, <em>Correct and include</em> lets
            you supply it here; the server checks the correction again on Execute.
          </p>
          {correctedAway > 0 ? (
            <p className="mut">
              {correctedAway.toLocaleString()} {correctedAway === 1 ? 'row is' : 'rows are'} corrected by hand and
              listed under <em>To ingest</em>.
            </p>
          ) : null}
          {groups.map(({ reason, rows }) => (
            <section className="skipgrp" key={reason}>
              <h4>
                {SKIP_REASON_COPY[reason].label}{' '}
                <span className="mut">— {rows.length.toLocaleString()}</span>
              </h4>
              <p className="why">{SKIP_REASON_COPY[reason].explains}</p>
              <div className="tblwrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th scope="col">File</th>
                      <th scope="col">Detail</th>
                      <th scope="col">
                        <span className="vh">Correct</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const key = rowKey(row);
                      return (
                        <SkippedRowFragment
                          key={key}
                          row={row}
                          editing={editing === key}
                          busy={busy}
                          onEdit={() => setEditing(key)}
                          onClose={() => setEditing(null)}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}

/** One skipped row, plus its inline correction editor when open (CLM-71). */
function SkippedRowFragment({
  row,
  editing,
  busy,
  onEdit,
  onClose,
}: {
  readonly row: SkippedDocument;
  readonly editing: boolean;
  readonly busy: boolean;
  readonly onEdit: () => void;
  readonly onClose: () => void;
}) {
  const key = rowKey(row);
  return (
    <>
      <tr>
        <FileCell file_name={row.file_name} web_url={row.web_url} folder_path={row.folder_path} />
        <td>
          {row.detail}
          {row.candidates.length > 0 ? (
            <span className="fl tagrow">
              {row.candidates.map((candidate) => (
                <span className="tag out" key={candidate}>
                  {candidate}
                </span>
              ))}
            </span>
          ) : null}
        </td>
        <td>
          {row.correctable && row.etag !== null ? (
            <button
              type="button"
              className="btn sm"
              disabled={busy || editing}
              aria-expanded={editing}
              aria-controls={editing ? `correct-${key}` : undefined}
              onClick={onEdit}
            >
              Correct and include
            </button>
          ) : null}{' '}
          {/* CLM-115. Deliberately NOT gated on `correctable`: `already_ingested`
              and `no_etag` are the two groups that can never be corrected and so
              return on every crawl forever, which is the noise this exists to
              clear. */}
          <button
            type="button"
            className="btn sm"
            disabled={busy || editing}
            onClick={() => {
              void skipRow(row);
            }}
          >
            Skip
          </button>
        </td>
      </tr>
      {editing ? (
        <CorrectionEditor
          row={row}
          current={null}
          disabled={busy}
          onApply={(override) => {
            setOverride(key, override);
            onClose();
          }}
          onCancel={onClose}
        />
      ) : null}
    </>
  );
}

/**
 * CLM-115 — the Skipped section.
 *
 * Two sources, deliberately shown as one list: `held` is what the server held
 * back from this crawl, and `skippedThisPlan` is what the reviewer has just
 * skipped in this sitting. They cannot overlap — a row the server held back
 * never reached `to_skip` — and a reviewer does not care which side of a
 * re-crawl their decision fell on.
 *
 * `changed_since_skip` is the safeguard on D-115-1. A skip survives a re-sign,
 * so a document skipped *because* it was wrong and then fixed would otherwise
 * be invisible. It stays out of the review list, and says so here instead.
 */
function SkippedCard({
  held,
  skippedThisPlan,
  busy,
}: {
  readonly held: PlanSharepointIngestResponse['user_skipped'];
  readonly skippedThisPlan: readonly SkippedThisPlanRow[];
  readonly busy: boolean;
}) {
  const total = held.length + skippedThisPlan.length;

  return (
    <div className="card">
      <h3>Skipped — {total.toLocaleString()}</h3>
      {total === 0 ? (
        <p>
          Nothing skipped. Use <strong>Skip</strong> on a row you have reviewed but do not want to
          correct — it will stay out of every future plan of this folder until you reopen it here.
        </p>
      ) : (
        <>
          <p>
            Reviewed and set aside. These stay out of the plan on every crawl of this folder, for
            everyone, until somebody reopens them. Reopening puts a document back in front of the
            next plan — it does not ingest anything.
          </p>
          <table>
            <caption className="vis-hidden">Documents held back by a reviewer</caption>
            <thead>
              <tr>
                <th scope="col">File</th>
                <th scope="col">Skipped</th>
                <th scope="col">Reason when skipped</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {skippedThisPlan.map((row) => (
                <tr key={rowKey(row)}>
                  <FileCell
                    file_name={row.file_name}
                    web_url={row.web_url}
                    folder_path={row.folder_path}
                  />
                  <td className="mut">just now, by you</td>
                  <td className="mut">{PLANNER_REASON_LABEL[row.reason]}</td>
                  <td>
                    <button
                      type="button"
                      className="btn sm"
                      disabled={busy}
                      onClick={() => {
                        void reopenRow(row);
                      }}
                    >
                      Reopen
                    </button>
                  </td>
                </tr>
              ))}
              {held.map((row) => (
                <tr key={rowKey(row)}>
                  <td>
                    {row.web_url ? (
                      <a href={row.web_url} target="_blank" rel="noreferrer">
                        {row.file_name}
                      </a>
                    ) : (
                      row.file_name
                    )}
                    {row.changed_since_skip ? (
                      <>
                        {' '}
                        <span
                          className="tag warn"
                          title="Re-signed or replaced since it was skipped. It stays out of the plan — reopen it if the change matters."
                        >
                          changed since skipped
                        </span>
                      </>
                    ) : null}
                    <span className="path">{row.folder_path.join(' / ')}</span>
                  </td>
                  <td className="mut">
                    {row.skipped_utc.slice(0, 10)} by {row.skipped_by_upn}
                  </td>
                  <td className="mut">{PLANNER_REASON_LABEL[row.planner_reason]}</td>
                  <td>
                    <button
                      type="button"
                      className="btn sm"
                      disabled={busy}
                      onClick={() => {
                        void reopenRow(row);
                      }}
                    >
                      Reopen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function ExcludedCard({ files }: { readonly files: readonly ExcludedFile[] }) {
  if (files.length === 0) return null;
  return (
    <details className="card secondary">
      <summary>
        Excluded by the “Signed” + PDF filter — {files.length.toLocaleString()}{' '}
        {files.length === 1 ? 'file' : 'files'}. Open to check nothing was missed (a
        “Countersigned” lands here). Tick ‘Include PDFs without a signed marker’ and plan again to
        make these candidates.
      </summary>
      <div className="tblwrap">
        <table className="tbl">
          <thead>
            <tr>
              <th scope="col">File</th>
              <th scope="col">Why</th>
            </tr>
          </thead>
          <tbody>
            {files.map((file) => (
              <tr key={`${file.drive_id}:${file.item_id}`}>
                <FileCell file_name={file.file_name} web_url={null} folder_path={file.folder_path} />
                <td>
                  {EXCLUSION_REASON_COPY[file.reason]}
                  {file.matched_word ? (
                    <span className="mut"> — found “{file.matched_word}”</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
